import { useState } from "react";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Alert from "@mui/material/Alert";

const today = dayjs();

export const DateRangeModal = ({
  open,
  handleClose,
  handleDateRange,
  availableDates,
  fixedPeriodStartDate = null,
  fixedPeriodEndDate = null,
  chargeType,
  setChargeType,
  rates,
  existingBookings = []
}) => {
  const [berthAvailableFrom, setBerthAvailableFrom] = useState(null);
  const [berthAvailableTo, setBerthAvailableTo] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [date, setDate] = useState("");
  const [allDatesDisabled, setAllDatesDisabled] = useState(false);

  // Helper function to check if fixed pricing period has any bookings
  const hasBookingInFixedPeriod = () => {
    if (!fixedPeriodStartDate || !fixedPeriodEndDate || !existingBookings || existingBookings.length === 0) {
      return false;
    }

    const fixedStart = dayjs(fixedPeriodStartDate);
    const fixedEnd = dayjs(fixedPeriodEndDate);

    // Check if any booking overlaps with fixed pricing period
    return existingBookings.some(booking => {
      const bookingStart = dayjs(booking.startDate || booking.bookingDate?.startDate);
      const bookingEnd = dayjs(booking.endDate || booking.bookingDate?.endDate);

      // Check for overlap: booking overlaps if it starts before fixed period ends AND ends after fixed period starts
      return bookingStart.isBefore(fixedEnd) || bookingEnd.isAfter(fixedStart);
    });
  };

  // Helper function to check if monthly pricing is viable
  const isMonthlyPricingViable = () => {
    // If no slot selected, can't determine
    if (!berthAvailableFrom || !berthAvailableTo) {
      return false;
    }

    const slotStart = dayjs(berthAvailableFrom);
    const slotEnd = dayjs(berthAvailableTo);
    const slotDuration = slotEnd.diff(slotStart, 'day') + 1;

    // Slot must be at least 30 days long
    if (slotDuration < 30) {
      return false;
    }

    return true;
  };

  const handleRateTypeChange = (type) => {
    setChargeType(type);

    if (type === "day") {
      setStartDate(null);
      setEndDate(null);
    } else if (type === "month") {
      // Check if monthly pricing is viable for this slot
      if (!isMonthlyPricingViable()) {
        toast.warning("Monthly pricing requires a slot of at least 30 days. Please select a longer slot or choose per day pricing.");
        return;
      }
      setStartDate(null);
      setEndDate(null);
    } else if (type === "fixed") {
      // Check if there are existing bookings in fixed period
      if (hasBookingInFixedPeriod()) {
        toast.warning("Fixed pricing is not available as there are existing bookings within the fixed rate period");
        return;
      }

      // Check if fixed pricing period is still valid
      const fixedStart = dayjs(fixedPeriodStartDate);
      const fixedEnd = dayjs(fixedPeriodEndDate);

      // If entire period has passed, don't allow selection
      if (today.isAfter(fixedEnd)) {
        toast.warning("Fixed pricing period has expired");
        return;
      }

      // If start date has passed but end date is still in future
      // Allow booking from today to end date
      if (today.isAfter(fixedStart)) {
        setStartDate(today);
        setEndDate(fixedEnd);
        toast("Fixed pricing start date adjusted to today as original start date has passed");
      } else {
        // Normal case: start date is in future or today
        setStartDate(fixedStart);
        setEndDate(fixedEnd);
      }
    }
  };

  // Helper function to get current month duration
  const getMonthDuration = () => {
    if (!startDate || !endDate) return 0;
    return dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
  };

  const handleChange = (event) => {
    console.log("handle changes event")
    if (!event.target.value) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const { startDate, endDate } = event.target.value;
    setBerthAvailableFrom(startDate);
    setBerthAvailableTo(endDate);
    setDate(event.target.value);

    // Check if slot duration is sufficient for monthly bookings
    if (chargeType === "month") {
      const slotStart = dayjs(startDate);
      const slotEnd = dayjs(endDate);
      const slotDuration = slotEnd.diff(slotStart, 'day') + 1;

      setAllDatesDisabled(slotDuration < 30);
    } else {
      setAllDatesDisabled(false);
    }
  };

  const shouldDisableStartDate = (date) => {
    if (chargeType === "month") {
      const proposedEndDate = dayjs(date).add(29, "day");
      // Disable start dates where 30-day period would exceed available range

      return proposedEndDate.isAfter(dayjs(berthAvailableTo));
    }
    return false;
  };

  const disableChargeType = (type) => {
    // Disable if slot not selected yet
    if (!berthAvailableFrom || !berthAvailableTo) {
      return true;
    }

    // Disable if rate not available for this pricing type
    if (type === 'day' && !rates.chargesPerDay) return true;
    if (type === 'month' && !rates.chargesPerMonth) return true;
    if (type === 'fixed' && !rates.fixedTotalPrice) return true;

    // Disable monthly pricing if slot is too short (< 30 days)
    if (type === 'month' && !isMonthlyPricingViable()) {
      return true;
    }

    // Disable fixed pricing if entire period has expired
    if (type === 'fixed' && fixedPeriodEndDate) {
      if (today.isAfter(dayjs(fixedPeriodEndDate))) {
        return true;
      }
    }

    // Disable fixed pricing if there are any bookings in the fixed period
    if (type === 'fixed' && hasBookingInFixedPeriod()) {
      return true;
    }

    return false;
  }

  // const [dateArray, setDateArray] = useState([]);
  // const getDaysArray = (start, end) => {
  //   const arr = [];
  //   for (
  //     const dt = new Date(start);
  //     dt <= new Date(end);
  //     dt.setDate(dt.getDate() + 1)
  //   ) {
  //     arr.push(new Date(dt));
  //   }
  //   return arr;
  // };

  // console.log("available dates: ", availableDates);

  const handleConfirm = () => {
    // Validate pricing type selection
    if (!chargeType) {
      toast.error("Please select a pricing type");
      return;
    }

    // Validate date selection
    if (!startDate || !endDate) {
      toast.error("Please select booking dates");
      return;
    }

    // Validate fixed pricing dates are within valid period
    if (chargeType === "fixed") {
      const fixedStart = dayjs(fixedPeriodStartDate);
      const fixedEnd = dayjs(fixedPeriodEndDate);
      const selectedStart = dayjs(startDate);
      const selectedEnd = dayjs(endDate);

      // If original start date has passed, validate against today
      const effectiveStartDate = today.isAfter(fixedStart) ? today : fixedStart;

      // Validate selected dates are within effective period
      if (selectedStart.isBefore(effectiveStartDate) || selectedEnd.isAfter(fixedEnd)) {
        toast.error("Selected dates are outside the valid fixed pricing period");
        return;
      }
    }

    handleDateRange(startDate, endDate, chargeType);
    handleClose();
    return;
  };

  // const disabledDates = (date) => {
  //   const d1 = dayjs(date).format("YYYY-MM-DD");
  //   const d2 = dayjs(startDate).format("YYYY-MM-DD");
  //   // console.log("D1: ", d1);
  //   // console.log("D2: ", d2);

  //   // console.log("is date match: ", d1 === d2);

  //   // return dateArray.includes(d1);
  // };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="BerthDateSlotModal">
        <Grid container className="searchBoxContainer">
          <Typography variant="h5">Select berth availability period</Typography>
          <Button
            disabled={startDate === null || endDate === null}
            className="btn"
            variant="contained"
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Grid>
        <Typography variant="subtitle2">
          Berth available from:{" "}
          {(availableDates && availableDates.length > 0)
            ? dayjs(availableDates[0]?.startDate).format("YYYY-MM-DD")
            : "--"}{" "}
          to{" "}
          {(availableDates && availableDates.length > 0)
            ? dayjs(availableDates[availableDates.length - 1]?.endDate).format(
                "YYYY-MM-DD"
              )
            : "--"}
        </Typography>
       {/* {(!!chargeType && chargeType === "fixed") && (<div className="note">
          <Alert severity="info">
            Between dates: {dayjs(fixedPeriodStartDate).format("YYYY-MM-DD")} to {dayjs(fixedPeriodEndDate).format("YYYY-MM-DD")} the price will be fixed at €{fixedTotalPrice}
          </Alert>
        </div>)} */}
        {allDatesDisabled && (
          <div className="note">
            <Alert severity="warning">
              No valid start dates available in the selected slot for booking.
              The selected period is too short for a 30-day monthly booking.

              Please select a different slot.
            </Alert>
          </div>
        )}
        {/* <Grid container mt={2}> */}
        <FormControl sx={{ mt: 1, minWidth: 240 }} size="small">
          <InputLabel id="date-range-select">Select desire slot</InputLabel>
          <Select
            labelId="date-range-select"
            value={date}
            label="Select desire slot"
            onChange={handleChange}
          >
            {(availableDates && availableDates.length > 0) ? availableDates?.map((dates, key) => {
              return (
                <MenuItem key={key} value={dates}>
                  {dates?.startDate} to {dates?.endDate}
                </MenuItem>
              );
            }) : (
              <MenuItem value={null}>
                No slots available
              </MenuItem>
            )}
          </Select>
        </FormControl>
        {/* </Grid> */}
        <Grid item xs={12} sm={12} md={10} lg={10} xl={8}>
          {/* Pricing Type Selection */}
          <Grid item xs={12} sm={10} md={9} lg={8} xl={6} style={{ marginBottom: '16px' }}>
            <Typography variant="subtitle2" style={{ marginBottom: '8px', color: '#666' }}>
              Available Pricing
            </Typography>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              padding: '4px',
              width: '100%',
              gap: '2px'
            }}>
              {['day', 'month', 'fixed'].map((type) => (
                <button
                  key={type}
                  type="button"
                  disabled={disableChargeType(type)}
                  onClick={() => handleRateTypeChange(type)}
                  style={{
                    padding: '12px 8px',
                    border: 'none',
                    backgroundColor: chargeType === type ? '#000' : 'transparent',
                    cursor: disableChargeType(type) ? 'not-allowed' : 'pointer',
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '14px',
                    color: chargeType === type ? '#fff' : '#666',
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    opacity: disableChargeType(type) ? 0.5 : 1
                  }}
                >
                  {type === 'day' ? 
                    (
                      !!rates.chargesPerDay ? 
                      `€${rates.chargesPerDay}/Day` : 
                      "/Day (Not Available)"
                    ) 
                    : type === 'month' ? 
                    (
                      !!rates.chargesPerMonth ? 
                      `€${rates.chargesPerMonth}/Month` : 
                      "/Month (Not Available)"
                    ) 
                    : (
                      !!rates.fixedTotalPrice ? 
                      `€${rates.fixedTotalPrice} Fixed Rate` : 
                      "Fixed Rate (Not Available)"
                    )}
                </button>
              ))}
            </div>
          </Grid>
          {/* Helper message when no slot selected */}
          {(!berthAvailableFrom || !berthAvailableTo) && (
            <Typography variant="caption" style={{ marginTop: '8px', color: '#666', display: 'block' }}>
              Please select an availability slot above first
            </Typography>
          )}

          {/* Warning when monthly pricing not viable */}
          {berthAvailableFrom && berthAvailableTo && !isMonthlyPricingViable() && rates.chargesPerMonth && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Monthly pricing is not available as the selected slot is shorter than 30 days. Please select a longer slot or choose per day pricing.
            </Alert>
          )}

          {/* Warning when fixed pricing has bookings */}
          {berthAvailableFrom && berthAvailableTo && hasBookingInFixedPeriod() && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Fixed pricing is not available as there are existing bookings within the fixed rate period
            </Alert>
          )}

          {/* Info alert for fixed pricing */}
          {chargeType === 'fixed' && startDate && endDate && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                Fixed Rate Period: {dayjs(fixedPeriodStartDate).format("MMM DD, YYYY")} to {dayjs(fixedPeriodEndDate).format("MMM DD, YYYY")}
              </Typography>
            </Alert>
          )}
        </Grid>
        <Grid container className="datePickerContainer" mt={2} mb={8}>
          <Grid item mt={2} size={{ xs: 12, md: 6 }}>
            <DemoItem label="Start Date">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={berthAvailableFrom === null || chargeType === "fixed"}
                  minDate={
                    today > dayjs(berthAvailableFrom) ? 
                      today : dayjs(berthAvailableFrom)
                  }
                  maxDate={dayjs(berthAvailableTo)}
                  shouldDisableDate={shouldDisableStartDate}
                  fullWidth
                  name="berthAvailableDate"
                  required
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e)
                    if (chargeType === "month") {
                      // Auto-select 30 days (29 days added to start date)
                      const proposedEndDate = dayjs(e).add(29, "day");
                      // Ensure it doesn't exceed available range
                      if (proposedEndDate.isAfter(dayjs(berthAvailableTo))) {
                        setEndDate(dayjs(berthAvailableTo));
                      } else {
                        setEndDate(proposedEndDate);
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </DemoItem>
          </Grid>
          <Grid item mt={2} size={{ xs: 12, md: 6 }}>
            <DemoItem label="End Date">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={startDate === null || chargeType === "month" || chargeType === "fixed"}
                  // defaultValue={today}
                  minDate={startDate}
                  maxDate={dayjs(berthAvailableTo)}
                  // shouldDisableDate={isWeekend}
                  fullWidth
                  name="berthAvailableDate"
                  required
                  value={endDate}
                  onChange={(e) => setEndDate(e)}
                />
              </LocalizationProvider>
            </DemoItem>
          </Grid>

          {/* Month Duration Selection for Monthly Bookings */}
          {chargeType === "month" && startDate && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ marginBottom: '8px', marginTop: '16px' }}>
                Booking Duration:
              </Typography>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '8px',
                marginTop: '8px'
              }}>
                <Button
                  className={getMonthDuration() === 30 ? "btn" : ""}
                  variant={getMonthDuration() === 30 ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    const newEndDate = dayjs(startDate).add(29, "day");
                    if (newEndDate.isAfter(dayjs(berthAvailableTo))) {
                      setEndDate(dayjs(berthAvailableTo));
                    } else {
                      setEndDate(newEndDate);
                    }
                  }}
                  disabled={dayjs(startDate).add(29, "day").isAfter(dayjs(berthAvailableTo))}
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    ...(getMonthDuration() !== 30 && {
                      borderColor: '#000',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    })
                  }}
                >
                  1 Month (30 days)
                </Button>
                <Button
                  className={getMonthDuration() === 60 ? "btn" : ""}
                  variant={getMonthDuration() === 60 ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    const newEndDate = dayjs(startDate).add(59, "day");
                    if (newEndDate.isAfter(dayjs(berthAvailableTo))) {
                      setEndDate(dayjs(berthAvailableTo));
                    } else {
                      setEndDate(newEndDate);
                    }
                  }}
                  disabled={dayjs(startDate).add(59, "day").isAfter(dayjs(berthAvailableTo))}
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    ...(getMonthDuration() !== 60 && {
                      borderColor: '#000',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    })
                  }}
                >
                  2 Months (60 days)
                </Button>
                <Button
                  className={getMonthDuration() === 90 ? "btn" : ""}
                  variant={getMonthDuration() === 90 ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    const newEndDate = dayjs(startDate).add(89, "day");
                    if (newEndDate.isAfter(dayjs(berthAvailableTo))) {
                      setEndDate(dayjs(berthAvailableTo));
                    } else {
                      setEndDate(newEndDate);
                    }
                  }}
                  disabled={dayjs(startDate).add(89, "day").isAfter(dayjs(berthAvailableTo))}
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    ...(getMonthDuration() !== 90 && {
                      borderColor: '#000',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    })
                  }}
                >
                  3 Months (90 days)
                </Button>
                <Button
                  className={getMonthDuration() === 120 ? "btn" : ""}
                  variant={getMonthDuration() === 120 ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    const newEndDate = dayjs(startDate).add(119, "day");
                    if (newEndDate.isAfter(dayjs(berthAvailableTo))) {
                      setEndDate(dayjs(berthAvailableTo));
                    } else {
                      setEndDate(newEndDate);
                    }
                  }}
                  disabled={dayjs(startDate).add(119, "day").isAfter(dayjs(berthAvailableTo))}
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    ...(getMonthDuration() !== 120 && {
                      borderColor: '#000',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    })
                  }}
                >
                  4 Months (120 days)
                </Button>
              </div>
              <Typography variant="caption" style={{ marginTop: '8px', display: 'block', color: '#666' }}>
                Current selection: {getMonthDuration()} days
                {getMonthDuration() % 30 !== 0 && getMonthDuration() > 0 && (
                  <span style={{ color: '#f57c00' }}> (Not a complete month)</span>
                )}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Modal>
  );
};
